import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Portal } from 'react-portal';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { Container, Segment, Button, Message } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import Error from '@plone/volto/components/theme/Error/Error';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import backSVG from '@plone/volto/icons/back.svg';
import {
  updateBezug,
  getBezugByID,
  getBezuege,
  deleteBezug,
  getVistPermissions,
} from '../../../actions';
import { hasManagementUiAccess } from '../../../helpers/permissions';

import ButtonArea from '../ButtonArea';

import BezugForm from './BezugForm';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
});

const BezugEdit = ({
  pathname,
  bezug,
  bezuege,
  getBezuege,
  getBezugByID,
  updateBezug,
  deleteBezug,
  intl,
  token,
  permissions = null,
  getVistPermissions,
}) => {
  const [formData, setFormData] = useState({
    name: '',
  });
  const location = useLocation();
  const [error, setError] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [isBezugNameDuplicate, setIsBezugNameDuplicate] = useState(false);
  const isSubmitDisabled = formData.name === '';

  const history = useHistory();
  // Management-table CRUD: Admin + F&E per TASK-90 §2.
  const canEdit = hasManagementUiAccess(permissions);
  // flag to make sure permissions are fully loaded before returning
  // an unauthorized error
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // load permissions
  useEffect(() => {
    setIsClient(true);
    // Fetch @vist_permissions if the reducer hasn't seen a response yet.
    if (!permissions && permissionsLoading) {
      getVistPermissions()
        .then(() => {
          setPermissionsLoading(false);
        })
        .catch(() => {
          setError({ status: 401 });
          setPermissionsLoading(false);
        });
    }
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    setIsClient(true);
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');
    getBezuege({ getAll: true });
    getBezugByID(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getBezugByID, getBezuege]);

  useEffect(() => {
    if (bezug) {
      setFormData({
        name: bezug.name,
      });
    }
  }, [bezug]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));

    if (name === 'name' && value !== bezug.name) {
      const isDuplicate = bezuege.some((bezug) => bezug.name === value);
      setIsBezugNameDuplicate(isDuplicate);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');

    try {
      await updateBezug(id, formData);
      history.push(`/controlpanel/rezeptdatenbank/bezuege`);
      // Handle success or show a success message
    } catch (error) {
      // Handle error during update
      setError(error.message);
    }
  };

  const handleDelete = async () => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');

    try {
      await deleteBezug(id);
      setDeleteSuccess(true);
    } catch (error) {
      // Handle delete error
      setError(error.message);
    }
  };

  if (!token) {
    return <Error error={{ status: 401 }} />;
  }
  if (!permissionsLoading && !canEdit) {
    return <Error error={{ status: 401 }} />;
  }
  return (
    <div className="view-wrapper">
      <Helmet title="Bezug Bearbeiten" />
      <Container className="controlpanel">
        <Segment.Group raised>
          <Segment className="primary">
            <div className="rezeptdatenbank header">
              <div className="header-container">
                <h1>{bezug && bezug.name} </h1>
              </div>
              <div className="button-container">
                <ButtonArea
                  handleSubmit={handleSubmit}
                  handleDelete={handleDelete}
                  isSubmitDisabled={isSubmitDisabled}
                  backURL={'/controlpanel/rezeptdatenbank/bezuege'}
                />
              </div>
            </div>
          </Segment>
          <Segment className="rdbAddEditForm">
            {error && (
              <Message negative>
                <Message.Header>Error</Message.Header>
                <p>{error}</p>
              </Message>
            )}
            {deleteSuccess ? (
              <Message positive>
                <Message.Header>Bezug wurde gelöscht</Message.Header>
                <Button
                  primary
                  as={Link}
                  to="/controlpanel/rezeptdatenbank/bezuege"
                >
                  Zurück zur Bezuege-Übersicht
                </Button>
              </Message>
            ) : (
              <>
                <div className="rdb edit form bezuege">
                  <BezugForm
                    formData={formData}
                    isBezugNameDuplicate={isBezugNameDuplicate}
                    handleInputChange={handleInputChange}
                  />
                </div>
              </>
            )}
          </Segment>
        </Segment.Group>
      </Container>
      {isClient && (
        <Portal node={document.getElementById('toolbar')}>
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <Link to="/controlpanel/rezeptdatenbank" className="item">
                <Icon
                  name={backSVG}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </Link>
            }
          />
        </Portal>
      )}
    </div>
  );
};

BezugEdit.propTypes = {
  pathname: PropTypes.string.isRequired,
  bezuege: PropTypes.array.isRequired,
  bezug: PropTypes.object,
  getBezugByID: PropTypes.func.isRequired,
  updateBezug: PropTypes.func.isRequired,
  deleteBezug: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  token: PropTypes.string,
  permissions: PropTypes.object,
  getVistPermissions: PropTypes.func,
};

export default compose(
  injectIntl,
  connect(
    (state) => ({
      pathname: state.router.location.pathname,
      bezug: state.bezugByID.data,
      bezuege: state.bezuege.data.bezuege,
      token: state.userSession.token,
      permissions: state.permissions.permissions,
    }),
    { updateBezug, getBezugByID, deleteBezug, getBezuege, getVistPermissions },
  ),
)(BezugEdit);
